import { BestPractices } from '../BestPractices/BestPractices'
import { Box, IconButton, Stack, Typography } from '@dtx-company/design-system/src'
import { DEFAULT_FLOWCODE_DESTINATION } from '../../../constants/flowcode'
import { Dialog } from '@mui/material'
import { FC, useCallback, useId, useState } from 'react'
import { Flowcode } from '@dtx-company/flowcode-generator-browser'
import { fireAnalyticsEvent } from '@dtx-company/inter-app/src/event-tracking/helpers/fireAnalyticsEvent'
import { getFlowcodeGeneratorRootUrl } from '@dtx-company/true-common/src/utils/urls/services'
import { selectDisplayScanDestination } from '@dtx-company/app/shared/code/src/machines/locg/locgSelectors'
import { useDimensions } from '../../../hooks/useDimensions'
import { useFlowcodeOptions } from '../hooks/useFlowcodeOptions'
import { useGlobalServices } from '@dtx-company/app/shared/common/src/hooks/useGlobalServices'
import { useLOCGPrismicContext } from '../hooks/useLOCGPrismicContext'
import { useScreenSizeBreakpoints } from '@dtx-company/app/shared/common/src/hooks/useScreenSizeBreakpoints'
import { useSelector } from '@xstate/react'
import InfoOutlined from '@mui/icons-material/InfoOutlined'
import useTranslation from 'next-translate/useTranslation'

export const CodePreview: FC<{ accordionPreviewStyle?: boolean }> = ({ accordionPreviewStyle }) => {
  const { locgService } = useGlobalServices()
  const options = useFlowcodeOptions()
  const displayScanDestination = useSelector(locgService, selectDisplayScanDestination)
  const { isMobile } = useScreenSizeBreakpoints()
  const [ref] = useDimensions()
  const {
    locgV2Data: {
      code_preview: [captions]
    }
  } = useLOCGPrismicContext()

  const fallbackFlowcodeImage = `${getFlowcodeGeneratorRootUrl()}/v1/flowcode?opts=${encodeURIComponent(
    JSON.stringify({
      ...options,
      data: options.data ?? DEFAULT_FLOWCODE_DESTINATION,
      radiusMask: options.radiusMask ?? 89
    })
  )}`

  const preview = (
    <Stack
      alignItems="center"
      component="figure"
      gap={accordionPreviewStyle ? 0 : 4}
      sx={theme => ({
        m: 0,
        pt: accordionPreviewStyle ? 2 : 0,
        pb: accordionPreviewStyle ? 2 : 0,
        height: '100%',
        width: '100%',
        figcaption: {
          width: { md: '80%', lg: theme.spacing(120) },
          gap: 2,
          mb: 4,
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          // prevent layout shift when a scan destination is added
          '& > p': {
            minHeight: theme => theme.spacing(4)
          }
        }
      })}
    >
      {accordionPreviewStyle && (
        <Typography
          variant={'captionMedium'}
          align="center"
          component="figcaption"
          data-testid="code-preview-caption"
        >
          {captions?.caption_line_1}
        </Typography>
      )}
      <Box
        data-testid={'item'}
        sx={theme => ({
          margin: '0 auto',
          flex: '1 0 8rem',
          aspectRatio: '1 /1',
          maxWidth: '100%',
          maxHeight: accordionPreviewStyle ? '80%' : '22rem',
          minWidth: theme.spacing(15),
          minHeight: theme.spacing(15),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          img: {
            objectFit: 'contain'
          }
        })}
        ref={ref}
      >
        <Flowcode options={options} fallbackImageURL={fallbackFlowcodeImage} />
      </Box>
      {!accordionPreviewStyle && (
        <Typography
          variant="captionMedium"
          align="center"
          component="figcaption"
          data-testid="code-preview-caption"
        >
          {captions?.caption_line_1}
          <br />
          {captions?.caption_line_2}
          <Typography
            variant="labelSmall"
            color="onSurface.primary"
            align="center"
            component="p"
            noWrap
          >
            {displayScanDestination}
          </Typography>
        </Typography>
      )}
    </Stack>
  )

  return (
    <Stack
      alignItems="center"
      justifyContent="space-between"
      sx={{
        position: 'relative',
        flex: {
          md: 0.85,
          lg: 1
        },
        overflowY: {
          md: 'auto'
        },
        py: {
          xs: 6,
          md: accordionPreviewStyle ? 0 : 10
        },
        height: {
          xs: '30vh',
          md: '100%'
        },
        backgroundColor: 'surface.2'
      }}
      gap={4}
    >
      {preview}
      {/* Desktop callout */}
      {!isMobile && !accordionPreviewStyle && (
        <Box
          sx={theme => ({
            display: { xs: 'none', md: 'block' },
            width: {
              md: '90%',
              lg: theme.spacing(120)
            }
          })}
        >
          <BestPractices />
        </Box>
      )}
      {isMobile && <MobileCodePreview />}
    </Stack>
  )
}

const MobileCodePreview: FC = () => {
  const { locgService } = useGlobalServices()
  const { t } = useTranslation('logged-out-generator')
  const [isOpen, setIsOpen] = useState(false)
  const handleClose = useCallback(() => {
    setIsOpen(false)
    fireAnalyticsEvent('LoggedOutGenerator_CodePreview_BestPracticesDialogClosed')
  }, [])
  const calloutTitleId = useId()
  const calloutDescriptionId = useId()

  const authSectionActive = useSelector(locgService, state => state.matches('auth'))

  return (
    <Box
      sx={theme => ({
        position: 'absolute',
        top: theme.spacing(3),
        right: theme.spacing(4),
        display: { xs: authSectionActive ? 'none' : 'block', md: 'none' }
      })}
    >
      <IconButton
        color="secondary"
        icon={InfoOutlined}
        onClick={() => setIsOpen(true)}
        aria-label={t('LoggedOutGenerator.CodePreview.calloutIconLabel')}
        data-heap-id="LoggedOutGenerator_CodePreview_BestPracticesButton"
      />
      {isOpen && (
        <Dialog
          hideBackdrop
          open={isOpen}
          onClose={handleClose}
          aria-labelledby={calloutTitleId}
          aria-describedby={calloutDescriptionId}
          fullWidth
        >
          <BestPractices
            titleId={calloutTitleId}
            descriptionId={calloutDescriptionId}
            handleClose={handleClose}
          />
        </Dialog>
      )}
    </Box>
  )
}
